import {useForm as useTanStackForm} from '@tanstack/react-form';
import * as React from 'react';

import type {FormValues} from './types';

/**
 * Builds the underlying TanStack Form instance.
 *
 * This is a hook of its own so that `FormInstance` can be written as its return
 * type. `useForm` has a dozen type parameters that are inferred from the
 * options object, and spelling them out by hand does not reproduce what
 * inference produces.
 */
export const useFormInstance = (
  defaultValues: FormValues,
  onSubmit: (values: FormValues) => unknown,
) =>
  useTanStackForm({
    defaultValues,
    // Returning the handler's result lets the form await it, so a form stays
    // in its submitting state until an async handler finishes.
    onSubmit: ({value}) => onSubmit(value),
  });

export type FormInstance = ReturnType<typeof useFormInstance>;

export type FormContextValue = {
  /**
   * Whether clearing a field stores `''` instead of dropping the value. Set per
   * form; see `Form`'s `allowEmptyStrings`.
   */
  allowEmptyStrings: boolean;
  form: FormInstance;
};

export const FormContext = React.createContext<FormContextValue | null>(null);

export const useFormContext = (): FormContextValue => {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error('Form hooks may only be used inside a <Form>');
  }
  return context;
};

/**
 * The TanStack Form instance backing the surrounding `<Form>`, for the cases
 * the hooks in this package don't cover — `form.Field`, `form.Subscribe`, and
 * the rest of the TanStack API.
 */
export const useForm = (): FormInstance => useFormContext().form;
